/*
* Palo Alto Theme — Archival Apparel custom JavaScript
*
* Keeping custom JS here (rather than in layout/theme.liquid) means it survives
* theme updates. Loaded by one line in layout/theme.liquid:
*
*     <script src="{{ 'custom.js' | asset_url }}" defer="defer"></script>
*/

(function () {
  'use strict';

  /* =====================================================================
   * Require an explicit size choice
   *
   * Shopify preselects the first available variant, which on our products
   * is always XS — the least-ordered size — producing accidental XS orders.
   * Rather than swap the default to another size (which relocates the
   * mistake to one customers are less likely to notice), no size is
   * preselected: the control reads "Select size", Add to cart prompts, and
   * the dynamic checkout button is hidden until a size is chosen.
   *
   * Skipped when the URL already names a variant (?variant=...), so links to
   * a specific size, the back button and shared URLs still win, and when a
   * product offers fewer than two sizes (totes, gift card).
   *
   * Implementation note: the theme re-renders the variant selectors on every
   * variant change, so everything here is delegated from `document` rather
   * than bound to elements, and the placeholder is re-applied by observer.
   * Binding directly to the option elements breaks on the first colour change.
   * ===================================================================== */

  var PLACEHOLDER = 'Select size';
  var PROMPT = 'Please select a size';
  var SIZE_RE = /options\[\s*size\s*\]/i;

  var active = false;   // true while awaiting a size choice

  function isSizeInput(el) {
    return el && typeof el.name === 'string' && SIZE_RE.test(el.name);
  }

  function isSizePopout(pop) {
    return !!pop && isSizeInput(pop.querySelector('[data-popout-input]'));
  }

  function sizePopouts() {
    return Array.prototype.filter.call(
      document.querySelectorAll('popout-select'), isSizePopout
    );
  }

  function sizeRadios() {
    return Array.prototype.filter.call(
      document.querySelectorAll('input[type="radio"]'), isSizeInput
    );
  }

  function countSizeChoices() {
    var pops = sizePopouts();
    var n = pops.reduce(function (acc, p) {
      return acc + p.querySelectorAll('a[data-popout-option]').length;
    }, 0);
    return n + sizeRadios().length;
  }

  function applyPlaceholder() {
    sizePopouts().forEach(function (pop) {
      var txt = pop.querySelector('[data-popout-text]');
      if (txt && txt.textContent !== PLACEHOLDER) txt.textContent = PLACEHOLDER;
      pop.classList.add('size-unset');
    });
    sizeRadios().forEach(function (r) { if (r.checked) r.checked = false; });
  }

  function showDynamicCheckout(show) {
    Array.prototype.forEach.call(
      document.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout'),
      function (el) { el.style.display = show ? '' : 'none'; }
    );
  }

  function showPrompt() {
    var pop = sizePopouts()[0];
    var radio = sizeRadios()[0];
    // Boxes: the message goes after the fieldset; dropdown: after the popout.
    var anchor = pop ? pop.parentElement
      : radio && (radio.closest('.selector-wrapper') || radio.closest('fieldset'));
    if (!anchor) return;
    if (!document.querySelector('[data-size-msg]')) {
      var msg = document.createElement('div');
      msg.setAttribute('data-size-msg', '');
      msg.className = 'size-required-msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = PROMPT;
      anchor.insertAdjacentElement(pop ? 'beforeend' : 'afterend', msg);
    }
    if (pop) {
      var toggle = pop.querySelector('[data-popout-toggle]');
      if (toggle) { toggle.focus(); toggle.click(); }
    } else {
      anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var first = anchor.querySelector('input[type="radio"]:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    }
  }

  function resolve() {
    if (!active) return;
    active = false;
    Array.prototype.forEach.call(document.querySelectorAll('.size-unset'),
      function (el) { el.classList.remove('size-unset'); });
    Array.prototype.forEach.call(document.querySelectorAll('[data-size-msg]'),
      function (el) { el.remove(); });
    showDynamicCheckout(true);
  }

  function onClick(e) {
    if (!active) return;

    // A size was chosen — stand down.
    var option = e.target.closest && e.target.closest('a[data-popout-option]');
    if (option && isSizePopout(option.closest('popout-select'))) { resolve(); return; }

    var radio = e.target.closest && e.target.closest('label, input[type="radio"]');
    if (radio) {
      var input = radio.tagName === 'INPUT'
        ? radio
        : document.getElementById(radio.getAttribute('for') || '') || radio.querySelector('input[type="radio"]');
      if (isSizeInput(input)) { resolve(); return; }
    }

    // Add to cart — including the sticky cart bar, whose button is
    // type="button" with its own handler rather than a form submit.
    var add = e.target.closest && e.target.closest('button[name="add"], [data-add-to-cart]');
    if (add) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showPrompt();
    }
  }

  function onSubmit(e) {
    if (!active) return;
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    showPrompt();
  }

  function init() {
    // Totes and the gift card have a one-value "Size" option: no size chart.
    if (countSizeChoices() < 2) document.documentElement.classList.add('aa-one-size');

    // A variant in the URL is an explicit choice — never override it.
    if (/[?&]variant=/.test(window.location.search)) return;
    if (countSizeChoices() < 2) return;

    active = true;
    applyPlaceholder();
    showDynamicCheckout(false);

    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);

    // The theme replaces the selector markup on every variant change, so
    // re-apply the placeholder (and re-hide dynamic checkout) after re-renders.
    if (!window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      if (!active) return;
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (!active) return;
        applyPlaceholder();
        showDynamicCheckout(false);
      }, 40);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    try { init(); }
    catch (err) { if (window.console && console.warn) console.warn('[custom.js] size guard:', err); }
    finally { if (window.aaSizeGuardLift) window.aaSizeGuardLift(); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

(function () {
  'use strict';

  /* =====================================================================
   * Show the selected colour's name
   *
   * Palo Alto renders colour swatches under a bare "Color" legend, so the
   * name of the chosen colour only exists as a tooltip. Heathers and near-
   * blacks (Asphalt, Vintage Black, Athletic Heather) are hard to tell apart
   * as dots, so write the name into the legend: "COLOR: VINTAGE BLACK".
   *
   * Same re-render caveat as the size guard: the theme swaps the selector
   * markup on every variant change, so this re-applies from an observer.
   * ===================================================================== */

  function paintColorNames() {
    Array.prototype.forEach.call(
      document.querySelectorAll('legend[data-swatches-label]'),
      function (legend) {
        var fieldset = legend.closest('fieldset');
        var checked = fieldset && fieldset.querySelector('input[type="radio"]:checked');
        var name = checked ? checked.value : '';
        var label = legend.querySelector('.radio__legend__label');
        if (!label) return;
        var span = label.querySelector('.aa-option-value');
        if (!span) {
          span = document.createElement('span');
          span.className = 'aa-option-value';
          label.appendChild(span);
        }
        var text = name ? ': ' + name : '';
        if (span.textContent !== text) span.textContent = text;
      }
    );
  }

  /* =====================================================================
   * Size chart pages: inch/cm toggle + highlight the chosen size
   *
   * Size charts are Shopify Pages shown in Palo Alto's size-chart drawer
   * (theme setting "info_page" for the default, product metafield
   * theme.size_chart for other blanks). Each page holds one table:
   *
   *   <div class="aa-size-chart">
   *     <table><thead>…</thead>
   *       <tbody><tr data-size="M"><th>M</th><td data-in="20">20</td>…
   *
   * Values are stored in inches only; centimetres are computed here.
   * ===================================================================== */

  var UNIT_KEY = 'aa-size-unit';

  function readUnit() {
    try { return localStorage.getItem(UNIT_KEY) === 'cm' ? 'cm' : 'in'; }
    catch (e) { return 'in'; }
  }

  function saveUnit(unit) {
    try { localStorage.setItem(UNIT_KEY, unit); } catch (e) { /* private mode */ }
  }

  // Garment specs are published to the nearest eighth of an inch; show those
  // as fractions (26⅝) rather than decimals (26.625), and centimetres to the
  // nearest half centimetre.
  var EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];

  function formatValue(inches, unit) {
    if (unit === 'cm') return String(Math.round(inches * 2.54 * 2) / 2);
    var eighths = Math.round(inches * 8);
    var whole = Math.floor(eighths / 8);
    var frac = EIGHTHS[eighths % 8];
    return (whole || !frac ? String(whole) : '') + frac;
  }

  function selectedSize() {
    // No highlight while the size guard is still waiting for a choice.
    if (document.querySelector('.size-unset')) return '';
    var radio = document.querySelector('input[type="radio"][name^="options[Size]"]:checked');
    if (radio) return radio.value;
    var hidden = document.querySelector('[data-popout-input][name^="options[Size]"]');
    return hidden ? hidden.value : '';
  }

  function renderChart(chart, unit) {
    chart.setAttribute('data-unit', unit);
    Array.prototype.forEach.call(chart.querySelectorAll('td[data-in]'), function (td) {
      var inches = parseFloat(td.getAttribute('data-in'));
      if (!isNaN(inches)) td.textContent = formatValue(inches, unit);
    });
    Array.prototype.forEach.call(chart.querySelectorAll('.aa-unit-toggle button'), function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-unit') === unit));
    });
    var size = selectedSize();
    Array.prototype.forEach.call(chart.querySelectorAll('tr[data-size]'), function (tr) {
      tr.classList.toggle('is-selected', !!size && tr.getAttribute('data-size') === size);
    });
  }

  function enhanceCharts() {
    var unit = readUnit();
    Array.prototype.forEach.call(document.querySelectorAll('.aa-size-chart'), function (chart) {
      if (!chart.querySelector('.aa-unit-toggle')) {
        var toggle = document.createElement('div');
        toggle.className = 'aa-unit-toggle';
        toggle.setAttribute('role', 'group');
        toggle.setAttribute('aria-label', 'Units');
        toggle.innerHTML =
          '<button type="button" data-unit="in">in</button>' +
          '<button type="button" data-unit="cm">cm</button>';
        chart.insertBefore(toggle, chart.firstChild);
      }
      renderChart(chart, unit);
    });
  }

  function onClick(e) {
    var btn = e.target.closest && e.target.closest('.aa-unit-toggle button');
    if (btn) {
      var unit = btn.getAttribute('data-unit');
      saveUnit(unit);
      Array.prototype.forEach.call(document.querySelectorAll('.aa-size-chart'), function (c) {
        renderChart(c, unit);
      });
      return;
    }
    // Opening the drawer: refresh the highlighted row for the current size.
    if (e.target.closest && e.target.closest('[aria-controls^="size-chart--"]')) {
      setTimeout(enhanceCharts, 0);
    }
  }

  function refresh() {
    paintColorNames();
    enhanceCharts();
  }

  function init() {
    refresh();
    document.addEventListener('click', onClick);
    document.addEventListener('change', function () { setTimeout(refresh, 0); });
    if (!window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(paintColorNames, 40);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    try { init(); }
    catch (err) { if (window.console && console.warn) console.warn('[custom.js] product extras:', err); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
